import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LandingPage from './LandingPage/LandingPage';
import PageLayout from './components/Layout/PageLayout';
import HeaderBar from './components/HeaderBar/HeaderBar';
import Footer from './components/Footer/Footer';
import { AdSenseProvider } from './ads';
import { Analytics } from '@vercel/analytics/react';

import './App.css';
import StaticChampionTracker from './StaticSandbox/pages/StaticChampionTracker';
import DamageCalculator from './NewWebSocket/pages/DamageCalculator';
import ComboInputPracticePage from './pages/ComboInputPractice/ComboInputPracticePage';
//import TeamfightBoard from './LandingPage/TeamfightSimulator/TeamfightGrid';
import TeamfightBoard2 from './LandingPage/TeamfightSimulator/TeamfightBoard';
import FarmingCalculatorPage from './pages/FarmingCalculatorPage';
import XPCalculatorPage from './pages/XPCalculatorPage';
import JungleSimulatorPage from './pages/JungleSimulator/JungleSimulatorPage';
import FeedbackPage from './pages/Feedback/FeedbackPage';
import MapEditorPage from './pages/MapEditor/MapEditorPage';
import MapViewerPage from './pages/MapViewer/MapViewerPage';
import RiftSimulatorPage from './pages/RiftSimulator/RiftSimulatorPage';
import TeamCompBuilderPage from './pages/TeamCompBuilder/TeamCompBuilderPage';
import NotFound from './pages/NotFound/NotFound';
import SummonerPage from './pages/Summoner/SummonerPage';
import MacroAnalysisPage from './pages/MacroAnalysis/MacroAnalysisPage';

const AppContent: React.FC = () => {
  const location = useLocation();
  
  // Determine header props based on current route
  const getHeaderProps = () => {
    switch (location.pathname) {
      case '/':
        return { showSearch: false, showDamageCalculatorButton: false };
      case '/damage-calculator':
        return { showSearch: true, showDamageCalculatorButton: false };
      case '/combo-practice':
        return { showSearch: true, showDamageCalculatorButton: true };
      case '/teamfight-simulator':
        return { showSearch: true, showDamageCalculatorButton: true };
      case '/farming-calculator':
        return { showSearch: true, showDamageCalculatorButton: true };
      case '/xp-calculator':
        return { showSearch: true, showDamageCalculatorButton: true };
      case '/jungle-simulator':
        return { showSearch: true, showDamageCalculatorButton: true };
      case '/map-editor':
        return { showSearch: false, showDamageCalculatorButton: true };
      case '/map-viewer':
        return { showSearch: false, showDamageCalculatorButton: true };
      case '/rift-simulator':
        return { showSearch: false, showDamageCalculatorButton: true };
      case '/feedback':
        return { showSearch: false, showDamageCalculatorButton: true };
      case '/macro-analysis':
        return { showSearch: false, showDamageCalculatorButton: true };
      case '/team-comp':
        return { showSearch: false, showDamageCalculatorButton: true };
      default:
        if (location.pathname.startsWith('/summoner/')) {
          return { showSearch: false, showDamageCalculatorButton: true };
        }
        return { showSearch: true, showDamageCalculatorButton: true };
    }
  };

  return (
    <div className="app-content-wrapper">
      <HeaderBar {...getHeaderProps()} />
        <div className="routes-container">
          <Routes>
          <Route path="/" element={
            <PageLayout>
              <LandingPage />
            </PageLayout>
          } />
            
          {/* Static Champion Tracker */}
          <Route path="/champions/:name" element={
            <PageLayout showAds={true}>
              <StaticChampionTracker />
            </PageLayout>
          } />
          
          {/* Damage Calculator */}
          <Route path="/damage-calculator" element={
            <PageLayout showAds={true}>
              <DamageCalculator />
            </PageLayout>
          } />
          
          {/* Combo Input Practice */}
          <Route path="/combo-practice" element={
            <PageLayout showAds={true}>
              <ComboInputPracticePage />
            </PageLayout>
          } />
          
          {/* Teamfight Simulator */}
          <Route path="/teamfight-simulator" element={
            <PageLayout showAds={true}>
              {/* <TeamfightBoard /> */}
              <TeamfightBoard2 />
            </PageLayout>
          } />
          
          {/* Farming Calculator */}
          <Route path="/farming-calculator" element={
            <PageLayout showAds={true}>
              <FarmingCalculatorPage />
            </PageLayout>
          } />
          
          {/* XP Calculator */}
          <Route path="/xp-calculator" element={
            <PageLayout showAds={true}>
              <XPCalculatorPage />
            </PageLayout>
          } />
          
          {/* Jungle Simulator */}
          <Route path="/jungle-simulator" element={
            <PageLayout showAds={true}>
              <JungleSimulatorPage />
            </PageLayout>
          } />

          {/* Map Viewer / Pathfinding */}
          <Route path="/map-viewer" element={
            <PageLayout>
              <MapViewerPage />
            </PageLayout>
          } />

          {/* Rift Simulator */}
          <Route path="/rift-simulator" element={
            <PageLayout>
              <RiftSimulatorPage />
            </PageLayout>
          } />

          {/* Team Comp Builder */}
          <Route path="/team-comp" element={
            <PageLayout showAds={true}>
              <TeamCompBuilderPage />
            </PageLayout>
          } />

          {/* Map Editor (Dev Tool) */}
          <Route path="/map-editor" element={
            <PageLayout>
              <MapEditorPage />
            </PageLayout>
          } />

          {/* Summoner Lookup */}
          <Route path="/summoner/:region/:gameName/:tagLine" element={
            <PageLayout showAds={true}>
              <SummonerPage />
            </PageLayout>
          } />

          {/* Bug Report / Feedback */}
          <Route path="/feedback" element={
            <PageLayout>
              <FeedbackPage />
            </PageLayout>
          } />

          {/* Macro Analysis MVP */}
          <Route path="/macro-analysis" element={
            <PageLayout>
              <MacroAnalysisPage />
            </PageLayout>
          } />

          {/* 404 Catch-all */}
          <Route path="*" element={
            <PageLayout>
              <NotFound />
            </PageLayout>
          } />
          </Routes>
        </div>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <AdSenseProvider>
        <Router>
          <AppContent />
        </Router>
      </AdSenseProvider>
      <Analytics />
    </HelmetProvider>
  );
};

export default App;
